import { useMemo } from 'react';
import { IconButton, Table, TableRow, TableHeadCell, TableCell, Pagination, PremiumTooltip, IconButtonWithText } from '../../../components/Common';
import { useTranslation } from '../../../hooks/useTranslation';
import { ProjectDtoRelV2 } from '../../../commonTypes/project.types';
import { useNavigate } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { selectAuth } from '../../../store/slices/auth.slice';
import { RoleTypeEnum } from '../../../commonTypes/user.types';
import { formatToMonthDayYear } from '../../../util/date/dateFormater';
import { ProjectStatusBadge } from './ProjectStatusBadge';
import { MemberAvatars } from './MemberAvatars';
import { useFeaturePlanLimits } from '../../../hooks/features';
import { Feature, ProjectStatus } from '@cyberlabrs/helpers';
import cc from 'classcat';
import { setSort, ProjectsSortBy, setPage, setElementsPerPage } from '../../../store/slices/projectsUi.slice';
import { RootState } from '../../../store';
import { isNotPrivileged } from '../../../api/utils/roleUtils';

interface ProjectsTableProps {
    projects: ProjectDtoRelV2[];
    searchTerm: string;
    selectedFilters: string[];
    clientId?: string;
}

export const ProjectsTable = ({ projects, searchTerm, selectedFilters, clientId }: ProjectsTableProps) => {
    const { t } = useTranslation('projects');
    const navigate = useNavigate();
    const authUser = useSelector(selectAuth);
    const isClientPage = Boolean(clientId);

    const dispatch = useDispatch();
    const { sortBy, sortDirection } = useSelector((state: RootState) => state.projectsUi);

    const context = isClientPage ? 'client' : 'normal';
    const { page, elementsPerPage } = useSelector((state: RootState) => state.projectsUi.pagination[context]);

    const { disabledItemIds: disabledProjectIds, shouldShowPremiumTooltip } = useFeaturePlanLimits(Feature.PROJECTS, 'create', projects);

    const canEdit =
        authUser.role.name !== RoleTypeEnum.ORDINARY && authUser.role.name !== RoleTypeEnum.BASIC && authUser.role.name !== RoleTypeEnum.TEAM_LEAD;

    const startIndex = page * elementsPerPage;
    const endIndex = startIndex + elementsPerPage;

    const handleSort = (column: ProjectsSortBy) => {
        dispatch(
            setSort({
                sortBy: column,
                sortDirection: column === sortBy ? (sortDirection === 'ASC' ? 'DESC' : 'ASC') : 'ASC',
            })
        );
    };

    const setPageHandler: React.Dispatch<React.SetStateAction<number>> = value => {
        const next = typeof value === 'function' ? value(page) : value;
        dispatch(setPage({ context, page: next }));
    };

    const setElementsPerPageHandler: React.Dispatch<React.SetStateAction<number>> = value => {
        const next = typeof value === 'function' ? value(elementsPerPage) : value;
        dispatch(setElementsPerPage({ context, value: next }));
    };

    const filtered = useMemo(() => {
        const q = searchTerm.toLowerCase().trim();

        let result = !q
            ? [...projects]
            : projects.filter(p => {
                  const n = p.name?.toLowerCase() ?? '';
                  const c = p.clientName?.toLowerCase() ?? '';
                  return n.includes(q) || c.includes(q);
              });

        if (selectedFilters.length > 0) {
            result = result.filter(project => {
                const projectStatus = project.projectStatus || (project.active ? 'InProgress' : 'Draft');
                return selectedFilters.includes(projectStatus);
            });
        }

        if (isNotPrivileged(authUser.role.name)) result = result.filter(project => project.projectStatus !== ProjectStatus.COMPLETED);

        return result;
    }, [projects, searchTerm, selectedFilters]);

    const sorted = useMemo(() => {
        const res = [...filtered];

        res.sort((a, b) => {
            const dir = sortDirection === 'ASC' ? 1 : -1;
            const toTime = (v: string | undefined) => (v ? new Date(v).getTime() : 0);

            switch (sortBy) {
                case 'NAME': {
                    return dir * a.name.localeCompare(b.name);
                }
                case 'CLIENT': {
                    return dir * (a.clientName || '').localeCompare(b.clientName || '');
                }
                case 'START_DATE': {
                    return dir * (toTime(a.startDate) - toTime(b.startDate));
                }
                case 'END_DATE': {
                    return dir * (toTime(a.endDate) - toTime(b.endDate));
                }
                case 'STATUS': {
                    return dir * (a.projectStatus || '').localeCompare(b.projectStatus || '');
                }
                default:
                    return 0;
            }
        });

        return res;
    }, [filtered, sortBy, sortDirection]);

    const paginatedProjects = sorted.slice(startIndex, endIndex);

    const handleViewProject = (projectId: string) => {
        if (disabledProjectIds.has(projectId)) {
            return;
        }
        navigate(`/project/${projectId}`);
    };

    const handleEditProject = (projectId: string) => {
        if (disabledProjectIds.has(projectId)) {
            return;
        }
        navigate(`/projects/${projectId}/edit`);
    };

    const handleCreateProject = () => {
        navigate('/create-project');
    };

    const getArrowClass = (column: typeof sortBy) => {
        if (sortBy !== column) return 'invoices__header-cell--sortable__arrow';
        return `invoices__header-cell--sortable__arrow invoices__header-cell--sortable__arrow--${sortDirection.toLowerCase()}`;
    };

    return (
        <div className="projects-table">
            <div className="projects-table__wrapper">
                <Table className="invoices__table">
                    <TableRow className="invoices__header-cell--background">
                        <TableHeadCell className="invoices__header-cell--background">
                            <div className="invoices__header-cell invoices__header-cell--sortable">
                                {t('project')}
                                <img
                                    src={sortBy === 'NAME' ? '/svg/common/sort-arrow-blue.svg' : '/svg/common/sort-arrow.svg'}
                                    className={getArrowClass('NAME')}
                                    onClick={() => handleSort('NAME')}
                                    alt="Sort"
                                />
                            </div>
                        </TableHeadCell>
                        {!isClientPage && (
                            <TableHeadCell className="invoices__header-cell--background">
                                <div className="invoices__header-cell invoices__header-cell--sortable">
                                    Client
                                    <img
                                        src={sortBy === 'CLIENT' ? '/svg/common/sort-arrow-blue.svg' : '/svg/common/sort-arrow.svg'}
                                        className={getArrowClass('CLIENT')}
                                        onClick={() => handleSort('CLIENT')}
                                        alt="Sort"
                                    />
                                </div>
                            </TableHeadCell>
                        )}
                        <TableHeadCell className="invoices__header-cell--background">
                            <div className="invoices__header-cell">{t('members')}</div>
                        </TableHeadCell>
                        <TableHeadCell className="invoices__header-cell--background">
                            <div className="invoices__header-cell invoices__header-cell--sortable">
                                Start Date
                                <img
                                    src={sortBy === 'START_DATE' ? '/svg/common/sort-arrow-blue.svg' : '/svg/common/sort-arrow.svg'}
                                    className={getArrowClass('START_DATE')}
                                    onClick={() => handleSort('START_DATE')}
                                    alt="Sort"
                                />
                            </div>
                        </TableHeadCell>
                        <TableHeadCell className="invoices__header-cell--background">
                            <div className="invoices__header-cell invoices__header-cell--sortable">
                                End Date
                                <img
                                    src={sortBy === 'END_DATE' ? '/svg/common/sort-arrow-blue.svg' : '/svg/common/sort-arrow.svg'}
                                    className={getArrowClass('END_DATE')}
                                    onClick={() => handleSort('END_DATE')}
                                    alt="Sort"
                                />
                            </div>
                        </TableHeadCell>
                        <TableHeadCell className="invoices__header-cell--background">
                            <div className="invoices__header-cell invoices__header-cell--sortable">
                                {t('status')}
                                <img
                                    src={sortBy === 'STATUS' ? '/svg/common/sort-arrow-blue.svg' : '/svg/common/sort-arrow.svg'}
                                    className={getArrowClass('STATUS')}
                                    onClick={() => handleSort('STATUS')}
                                    alt="Sort"
                                />
                            </div>
                        </TableHeadCell>
                        {!isClientPage && <TableHeadCell className="invoices__header-cell--background"></TableHeadCell>}
                    </TableRow>

                    {paginatedProjects.map((project, index) => {
                        const activeMembers = project.projectAssignees?.filter(assignee => assignee.active) || [];
                        const isDisabled = disabledProjectIds.has(project.id);

                        return (
                            <TableRow
                                key={project.id}
                                className={cc([
                                    'invoices__row',
                                    index % 2 === 0 ? 'invoices__row--white' : 'invoices__row--gray',
                                    !isDisabled && 'invoices__row--clickable',
                                    isDisabled && 'projects-table__row--disabled',
                                ])}
                                onClick={() => handleViewProject(project.id)}
                            >
                                <TableCell className="invoices__cell--background">
                                    <div className={cc(['invoices__cell', isDisabled && shouldShowPremiumTooltip && 'premium-tooltip-wrapper'])}>
                                        <span className={cc(['invoices__cell-text', isDisabled && 'projects-table__cell-text--disabled'])}>
                                            {project.name}
                                        </span>
                                        {isDisabled && shouldShowPremiumTooltip && <PremiumTooltip position="bottom" showOnHover />}
                                    </div>
                                </TableCell>
                                {!isClientPage && (
                                    <TableCell className="invoices__cell--background">
                                        <div className="invoices__cell">
                                            <span className="invoices__cell-text">{project.clientName || '-'}</span>
                                        </div>
                                    </TableCell>
                                )}
                                <TableCell className="invoices__cell--background">
                                    {activeMembers.length > 0 ? (
                                        <MemberAvatars members={activeMembers} maxDisplay={3} />
                                    ) : (
                                        <span className="invoices__cell-text">-</span>
                                    )}
                                </TableCell>
                                <TableCell className="invoices__cell--background">
                                    <div className="invoices__cell">
                                        <span className="invoices__cell-text">
                                            {project.startDate ? formatToMonthDayYear(project.startDate) : '-'}
                                        </span>
                                    </div>
                                </TableCell>
                                <TableCell className="invoices__cell--background">
                                    <div className="invoices__cell">
                                        <span className="invoices__cell-text">{project.endDate ? formatToMonthDayYear(project.endDate) : '-'}</span>
                                    </div>
                                </TableCell>
                                <TableCell className="invoices__cell--background">
                                    <ProjectStatusBadge status={project.projectStatus || (project.active ? 'InProgress' : 'Draft')} />
                                </TableCell>
                                {!isClientPage && (
                                    <TableCell className="invoices__cell--background">
                                        <div className="project-table__actions-cell">
                                            {canEdit && (
                                                <IconButton
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        handleEditProject(project.id);
                                                    }}
                                                    iconUrl="/svg/pen-grey.svg"
                                                    iconAlt="Edit Project"
                                                    variant="icon"
                                                    type="button"
                                                    disabled={isDisabled}
                                                />
                                            )}
                                            <IconButton
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    handleViewProject(project.id);
                                                }}
                                                iconUrl="/svg/eye-grey.svg"
                                                iconAlt="View Project"
                                                variant="icon"
                                                type="button"
                                                disabled={isDisabled}
                                            />
                                        </div>
                                    </TableCell>
                                )}
                            </TableRow>
                        );
                    })}

                    <TableRow className="projects-table__pagination-row">
                        <TableCell colSpan={7} className="projects-table__pagination-cell">
                            <Pagination
                                total={sorted.length}
                                elementsPerPage={elementsPerPage}
                                setElementsPerPage={setElementsPerPageHandler}
                                page={page}
                                setPage={setPageHandler}
                                isClientPage={isClientPage}
                            />
                        </TableCell>
                    </TableRow>
                </Table>
                {isClientPage && (
                    <div className="client-overview__add-button">
                        <IconButtonWithText
                            text="Add new project"
                            onClick={handleCreateProject}
                            disabled={false}
                            iconSrc="/svg/plus-blue-without-circle.svg"
                            variant="text"
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
